<template>
  <va-modal
    id="create-or-update-appointment-payment-modal"
    ref="createOrUpdateAppointmentPaymentModal"
    :value="isOpen"
    :okText="$t('modals.buttons.save')"
    :okDisabled="isSubmitingForm"
    :cancelText="$t('modals.buttons.back')"
    @ok="submitForm"
    @cancel="onCancel"
    size="medium"
    :noOutsideDismiss="true"
  >
    <div slot="header" class="modal-title d-flex align-items-center justify-content-between">
      <span>{{ isUpdate ? $t('homelab.labels.update_payment') : $t('homelab.labels.new_payment') }}</span>
    </div>

    <div class="modal-header-actions d-flex justify-content-end">
      <span class="text-primary clickable mt-2" @click="isShowingAppointmentInfo = !isShowingAppointmentInfo">
        {{isShowingAppointmentInfo ? $t('homelab.buttons.hide_appointment_info'): $t('homelab.buttons.see_appointment_info')}}
      </span>
    </div>

    <fade-transition :duration="350" mode="in-out">
      <div key="appointment-short-info" class="mb-3 w-100" v-if="isShowingAppointmentInfo">
        <AppointmentShortInfo
          :appointment="appointment"
        />
        <hr>
      </div>
    </fade-transition>

    <div class="evollu-form mb-3">
      <div class="row">
        <div class="col text-small">
          <span class="is-bold">{{$t('homelab.payment.note')}}: </span>
          <span>{{$t('homelab.payment.warning_about_payment')}}</span>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-md-6 mb-3">
          <DatePicker
            id="date-picker-payment-date"
            v-model="form.data.date"
            :label="$t('homelab.labels.payment_date')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            :clearable="false"
            @input="form.errors.clear('date')"
            :error="form.errors.has('date')"
            :error-messages="[$t(form.errors.get('date'))]"
          />
        </div>

        <div class="col-12 col-md-6 mb-3">
          <label class="form-label">
            {{ $t('homelab.labels.payment.status') }}:
          </label>
          <multiselect
            id="payment-status"
            v-model="form.data.payment_status"
            :options="paymentStatusesOptions"
            :customLabel="option => $t(`homelab.payment_statuses.${option.id}`)"
            track-by="id"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            @input="form.errors.clear('payment_status_id')"
          >
          </multiselect>
          <div class="text-danger" v-if="form.errors.has('payment_status_id')">
            <small>{{$t(form.errors.get('payment_status_id'))}}</small>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6 mb-3">
          <va-input
            id="appointment-price"
            :label="`${$t('homelab.labels.total_price')}€`"
            v-model="form.data.value"
            :placeholder="`${$t('homelab.labels.total_price')}€`"
            type="number"
            :max="9999"
            @input="form.errors.clear('value')"
            :error="form.errors.has('value')"
            :error-messages="[$t(form.errors.get('value'))]"
          />
        </div>

        <div class="col-12 col-md-6 mb-3">
          <label class="form-label">
            {{ $t('homelab.labels.payment.method') }}:
          </label>
          <multiselect
            id="payment-method"
            v-model="form.data.payment_method"
            :options="paymentMethodsOptions"
            :customLabel="option => $t(`homelab.labels.payment.methods.${option.id}`)"
            track-by="id"
            placeholder="---"
            :allow-empty="false"
            :multiple="false"
            :disabled="!canUpdateMethod"
            @input="form.errors.clear('payment_method_id')"
          >
          </multiselect>
          <div class="text-danger" v-if="form.errors.has('payment_method_id')">
            <small>{{$t(form.errors.get('payment_method_id'))}}</small>
          </div>
        </div>
      </div>

      <fade-transition :duration="350" mode="in-out">
        <section v-if="form.data.payment_method != null" key="payment-method-specific-fields">
          <div class="row" v-if="form.data.payment_method.id === paymentMethods.MB.id">
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="payment-mb-entity"
                :label="$t('homelab.labels.payment.mb_entity')"
                :placeholder="$t('homelab.labels.payment.mb_entity')"
                :maxlength="5"
                v-model="form.data.mb_entity"
                @input="form.errors.clear('mb_entity')"
                :error="form.errors.has('mb_entity')"
                :error-messages="[$t(form.errors.get('mb_entity'))]"
                :disabled="!canUpdateMethodSpecificFields"
              />
            </div>
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="payment-mb-reference"
                :label="$t('homelab.labels.payment.mb_reference')"
                :placeholder="$t('homelab.labels.payment.mb_reference')"
                :maxlength="9"
                v-model="form.data.mb_reference"
                @input="form.errors.clear('mb_reference')"
                :error="form.errors.has('mb_reference')"
                :error-messages="[$t(form.errors.get('mb_reference'))]"
                :disabled="!canUpdateMethodSpecificFields"
              />
            </div>
          </div>

          <div class="row" v-if="form.data.payment_method.id === paymentMethods.MBW.id">
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="payment-mbw-costumer-phone"
                :label="$t('homelab.labels.payment.customer_phone')"
                v-model="form.data.customer_phone"
                :placeholder="$t('homelab.labels.payment.customer_phone')"
                :maxlength="20"
                @input="form.errors.clear('customer_phone')"
                :error="form.errors.has('customer_phone')"
                :error-messages="[$t(form.errors.get('customer_phone'))]"
                :disabled="!canUpdateMethodSpecificFields"
              />
            </div>
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="payment-mbw-costumer-name"
                :label="$t('homelab.labels.payment.customer_name')"
                v-model="form.data.customer_name"
                :placeholder="$t('homelab.labels.payment.customer_name')"
                :maxlength="250"
                @input="form.errors.clear('customer_name')"
                :error="form.errors.has('customer_name')"
                :error-messages="[$t(form.errors.get('customer_name'))]"
                :disabled="!canUpdateMethodSpecificFields"
              />
            </div>
          </div>

          <div class="row" v-if="form.data.payment_method.id === paymentMethods.CC.id">
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="payment-cc-card-type"
                :label="$t('homelab.labels.payment.cc_card_type')"
                v-model="form.data.cc_card_type"
                :placeholder="`${$t('homelab.labels.payment.cc_card_type')} (Ex.: Visa, Mastercard)`"
                :maxlength="20"
                @input="form.errors.clear('cc_card_type')"
                :error="form.errors.has('cc_card_type')"
                :error-messages="[$t(form.errors.get('cc_card_type'))]"
                :disabled="!canUpdateMethodSpecificFields"
              />
            </div>
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="payment-cc-last-four"
                :label="$t('homelab.labels.payment.cc_last_four')"
                v-model="form.data.cc_last_four"
                :placeholder="$t('homelab.labels.payment.cc_last_four')"
                :maxlength="4"
                @input="form.errors.clear('cc_last_four')"
                :error="form.errors.has('cc_last_four')"
                :error-messages="[$t(form.errors.get('cc_last_four'))]"
                :disabled="!canUpdateMethodSpecificFields"
              />
            </div>
          </div>

          <div class="row" v-if="form.data.payment_method.id === paymentMethods.CC.id">
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="payment-cc-exp-date"
                :label="$t('homelab.labels.payment.cc_expiration_date')"
                v-model="form.data.cc_expiration_date"
                :placeholder="`${$t('homelab.labels.payment.cc_expiration_date')} (Ex.: 03/25)`"
                :maxlength="5"
                @input="form.errors.clear('cc_expiration_date')"
                :error="form.errors.has('cc_expiration_date')"
                :error-messages="[$t(form.errors.get('cc_expiration_date'))]"
                :disabled="!canUpdateMethodSpecificFields"
              />
            </div>
          </div>
        </section>
      </fade-transition>
    </div>
  </va-modal>
</template>
<script>
import { mapState } from 'vuex'
import Multiselect from 'vue-multiselect'
import Errors from '@/utils/CustomHomelabErrors'
import DatePicker from '@/components/common/DatePicker'
import { AppointmentPaymentService } from '@/api/HomelabService'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import AppointmentShortInfo from '@/components/homelab/appointments/details/AppointmentShortInfo'

export default {
  props: {
    appointment: {
      type: Object,
      required: false
    },
    payment: {
      type: Object,
      required: false
    }
  },
  components: {
    DatePicker,
    Multiselect,
    FadeTransition,
    AppointmentShortInfo
  },
  created: function () {
    if (this.isUpdate) {
      this.prepareForUpdate()
    } else {
      this.preFillForm()
    }
  },
  computed: {
    ...mapState('homelab', [
      'paymentTypes',
      'paymentMethods',
      'paymentStatuses'
    ]),
    isUpdate: function () {
      return this.payment !== null && this.payment !== undefined && this.payment !== ''
    },
    paymentTypesOptions: function () {
      return Object.values(this.paymentTypes)
    },
    paymentMethodsOptions: function () {
      return Object.values(this.paymentMethods).filter(method => method.id !== this.paymentMethods.PP.id)
    },
    paymentStatusesOptions: function () {
      let statuses = []
      if (this.form.data.payment_method != null && this.form.data.payment_method.id === this.paymentMethods.CC.id) {
        statuses = statuses.concat([
          this.paymentStatuses.PENDING,
          this.paymentStatuses.PAID,
          this.paymentStatuses.SUCCESS,
          // this.paymentStatuses.CAPTURE_SUCCESS,
          this.paymentStatuses.DECLINED,
          this.paymentStatuses.CAPTURE_FAILED,
          this.paymentStatuses.AUTHORIZATION_SUCCESS,
          this.paymentStatuses.AUTHORIZATION_FAILED
        ])
      } else {
        statuses = statuses.concat([
          this.paymentStatuses.PENDING,
          this.paymentStatuses.PAID
        ])
      }

      if (this.initialPaymentStatusId !== null && !statuses.some(status => status.id === this.initialPaymentStatusId)) {
        const status = this.paymentStatuses.find(status => status.id === this.initialPaymentStatusId)
        if (status) {
          statuses.push(status)
        }
      }
      return statuses
    },
    canUpdateMethod: function () {
      return !this.isUpdate || (this.isUpdate && this.payment.payment_method_id === null)
    },
    canUpdateMethodSpecificFields: function () {
      return !this.isUpdate || (this.isUpdate && (this.payment.payment_type_id === null || this.payment.payment_type_id === this.paymentTypes.IN_FACILITY.id))
    }
  },
  data: function () {
    return {
      isOpen: false,
      isSubmitingForm: false,
      isShowingAppointmentInfo: false,
      initialPaymentStatusId: this.isUpdate ? this.payment.payment_status_id : null,
      form: {
        data: {
          payment_type: null,
          payment_status: null,
          payment_method: null,
          date: null,
          value: null,
          customer_name: null,
          customer_phone: null,
          mb_entity: null,
          mb_reference: null,
          cc_last_four: null,
          cc_card_type: null,
          cc_expiration_date: null
        },
        errors: new Errors()
      }
    }
  },
  mounted: function () {
    this.isOpen = true
  },
  methods: {
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => this.$emit('cancel'), 500)
    },
    submitForm: function () {
      if (!this.isSubmitingForm) {
        let data = this.getFormData()
        this.isSubmitingForm = true
        AppointmentPaymentService.updatePayment(this.appointment.entity.id, this.appointment.id, data)
          .then(payment => {
            this.$emit('appointment-payment-updated', { payment })
            this.showToast(this.isUpdate ? this.$t('homelab.messages.payment_updated') : this.$t('homelab.messages.payment_created'), this.$store.state.successToastOptions)
            this.onCancel()
          })
          .catch(errors => {
            console.log(errors)
            this.showToast(this.$t('homelab.messages.form_contains_errors'), this.$store.state.errorToastOptions)
            this.form.errors.record(errors.response.data.errors, 'homelab.errors.')
          })
          .finally(() => {
            this.isSubmitingForm = false
          })
      }
    },
    getFormData: function () {
      let data = JSON.parse(JSON.stringify(this.form.data))
      data.payment_type_id = data.payment_type != null ? data.payment_type.id : null
      data.payment_method_id = data.payment_method != null ? data.payment_method.id : null
      data.payment_status_id = data.payment_status != null ? data.payment_status.id : null

      delete data.payment_type
      delete data.payment_method
      delete data.payment_status
      return data
    },
    preFillForm: function () {
      this.form.data.payment_type = this.paymentTypes.IN_FACILITY
      this.form.data.payment_status = this.paymentStatuses.PENDING
      this.form.data.customer_name = this.appointment.name || null
      this.form.data.customer_phone = this.appointment.phone_number || null
      this.form.data.value = this.appointment.total_price || null
      this.form.data.date = this.moment().format('YYYY-MM-DD')
    },
    prepareForUpdate: function () {
      const payment = this.appointment.payment
      if (payment) {
        if (payment.payment_type_id) {
          this.form.data.payment_type = this.paymentTypesOptions.find(type => type.id === payment.payment_type_id) || null
        } else {
          this.form.data.payment_type = this.paymentTypes.IN_FACILITY
        }
        this.form.data.payment_method = this.paymentMethodsOptions.find(method => method.id === payment.payment_method_id) || null

        if (payment.payment_status_id) {
          this.form.data.payment_status = this.paymentStatusesOptions.find(status => status.id === payment.payment_status_id) || null
        } else {
          this.form.data.payment_status = this.paymentStatuses.PENDING
        }

        this.form.data.date = payment.date
        this.form.data.value = payment.value
        this.form.data.customer_name = payment.customer_name
        this.form.data.customer_phone = payment.customer_name
        this.form.data.mb_entity = payment.mb_entity
        this.form.data.mb_reference = payment.mb_reference
        this.form.data.cc_last_four = payment.cc_last_four
        this.form.data.cc_card_type = payment.cc_card_type
        this.form.data.cc_expiration_date = payment.cc_expiration_date
      }
    }
  },
  watch: {
    'form.data.payment_method': {
      handler: function (value) {
        if (value !== null && value !== undefined && value.id === this.paymentMethods.MBW.id) {
          this.form.data.customer_name = this.appointment.name || null
          this.form.data.customer_phone = this.appointment.phone_number || null
        } else {
          this.form.data.customer_name = null
          this.form.data.customer_phone = null
        }
      },
      immediate: true
    },
    paymentStatusesOptions: function (options) {
      if (this.form.data.payment_status !== null && !options.some(status => status.id === this.form.data.payment_status.id)) {
        this.form.data.payment_status = null
      }
    }
  }
}
</script>
